import Toast from 'yofoui/components/toast';
import { NativeModules, Platform, DeviceEventEmitter } from 'yofobasement';
import protocol from './protocol';

const State = {
  IDLE: 'IDLE',
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',
  SESSION_ACTIVE: 'SESSION_ACTIVE',
  SESSION_IDLE: 'SESSION_IDLE',
  FINISHING: 'FINISHING',
  FINISHED: 'FINISHED'
};

export default class TTSPlayer {
  API_URL = 'wss://openspeech.bytedance.com/api/v3/tts/bidirection';
  CloseFrame = {
    NORMAL: 1000,
    ABNORMAL_CLOSE: 1006
  };

  // _appid = ''; // 9145638862
  // _accessToken = ''; // 3a1ScPkz5Rt-71YUU5qF1oiSDNCiJ0IV
  // _voice_type = ''; // 'zh_female_yuanqinvyou_moon_bigtts'
  _config = null;
  // 兼容老的 audio.ts：该字段用于判断是否为“同一个音频”，不能在播放过程中被消费或清空
  _filename = ''; // = raw text
  // 内部真正待播的首段完整文本，避免和 _filename 的老逻辑语义冲突
  _initialText = '';
  _playing = false;
  _callback = null;
  static instanceIndex = 0;
  _sessionID = '';
  _sessionTextQueue = [];
  _ws = null;
  _state = State.IDLE;
  _textBuffer = '';
  _textStreamDone = false;
  _connectionDone = false;
  _bufferFlushTimer = null;
  _bufferFlushDelay = 400;
  _isConnecting = false;
  _sessionActive = false;
  _waitingForSessionFinish = false;
  _dropCurrentSessionAudio = false;
  _lang = '';
  _nativePlayStarted = false;
  _nativePlayGeneration = 0;

  constructor(text = '') {
    this._filename = text;
    this._initialText = this._normalizeSpeechText(text);
    this._sessionID = this.uuidv4();
    if (this._initialText) {
      this._textStreamDone = true;
      this._connectionDone = true;
    }
  }

  init(config) {
    TTSPlayer.instanceIndex = TTSPlayer.instanceIndex + 1;
    console.log('ttsplayer init config:', TTSPlayer.instanceIndex);
    this._config = config;
  }

  pause() {
    console.log('ttsplayer pause');
    NativeModules.AudioStream.playOrPause(false);
    this._playing = false;
  }

  // 纯只读连接状态判断：供业务层在不依赖内部字段语义的前提下，判断当前 ws 是否仍可继续承载流式播报。
  isConnectionUsable() {
    if (this._ws == null) return false;
    const readyState =
      typeof this._ws.readyState === 'number' ? this._ws.readyState : WebSocket.CLOSED;
    if (readyState === WebSocket.CLOSING || readyState === WebSocket.CLOSED) return false;
    return (
      this._state !== State.IDLE &&
      this._state !== State.FINISHING &&
      this._state !== State.FINISHED
    );
  }

  // 流式场景下追加文本，先进入缓冲区，再按分句规则推进发送队列。
  appendText(chunk) {
    if (this._textStreamDone) return;
    const normalizedChunk = this._normalizeSpeechText(chunk);
    if (!normalizedChunk) return;
    this._textBuffer += normalizedChunk;
    this._extractChunksToQueue();
    this._tryFlushBuffer();
    this._scheduleBufferFlush();
  }

  play(callback, lang) {
    console.log('ttsplayer play', this._ws);
    if (callback) {
      this._callback = callback;
    }
    if (lang !== undefined) {
      this._lang = lang;
    }
    if (this._ws == null) {
      if (this._config?.appid == null || this._config?.appid == '') {
        console.log('ttsplayer appid is empty');
        return;
      }
      if (this._config?.accessToken == null || this._config?.accessToken == '') {
        console.log('ttsplayer accessToken is empty');
        return;
      }
      const connectId = this.uuidv4();
      console.log('ttsplayer ws X-Api-Connect-Id', connectId);
      this._ws = new WebSocket(this.API_URL, [], {
        headers: {
          'X-Api-App-Key': this._config?.appid,
          'X-Api-Access-Key': this._config?.accessToken,
          'X-Api-Resource-Id': this._config?.copyType
            ? 'volc.megatts.default'
            : 'volc.service_type.10029',
          'X-Api-Connect-Id': connectId
        }
      });
      this._isConnecting = true;
      this._state = State.CONNECTING;
      this._playing = true;
      this._play(callback, lang);
    } else {
      if (this._nativePlayStarted) {
        NativeModules.AudioStream.playOrPause(true);
      }
      this._playing = true;
      if (callback) callback(true);
    }
  }

  splitIntoQueue(str) {
    // 火山的session一次最大支持300个字符，所以要将长字符串分割
    const chunkSize = 300;
    const queue = [];
    const totalLength = str.length;
    for (let i = 0; i < totalLength; i += chunkSize) {
      const chunk = str.substring(i, i + chunkSize);
      queue.push(chunk);
    }
    return queue;
  }

  // 对输入文本做轻量清洗，避免 markdown 符号直接进入播报。
  _normalizeSpeechText(text = '') {
    if (!text) return '';

    return text
      .replace(/\r\n/g, '\n')
      .replace(/(^|\n)\s{0,3}#{1,6}\s+/g, '$1')
      .replace(/(^|\n)\s*[*-]\s+/g, '$1')
      .replace(/[*#]/g, '');
  }

  // 将缓冲区中的文本按句末标点或长度阈值切成可发送片段。
  _extractChunksToQueue() {
    const reg = /([。！？!?；;]+(?:\.{3}|\…+)?)/g;
    let match;

    if (this._textBuffer.length >= 100) {
      const chunk = this._textBuffer.substring(0, 100);
      this._sessionTextQueue.push(...this.splitIntoQueue(chunk));
      this._textBuffer = this._textBuffer.substring(100);
      return;
    }

    while ((match = reg.exec(this._textBuffer)) !== null) {
      const splitIndex = match.index + match[0].length;
      if (splitIndex >= 5) {
        const chunk = this._textBuffer.substring(0, splitIndex);
        this._sessionTextQueue.push(...this.splitIntoQueue(chunk));
        this._textBuffer = this._textBuffer.substring(splitIndex);
        reg.lastIndex = 0;
      }
    }
  }

  // 关闭或静默超时前，把最后一段未成句文本整体推进发送队列。
  _flushRemainingBuffer() {
    if (!this._textBuffer.length) return;
    this._sessionTextQueue.push(...this.splitIntoQueue(this._textBuffer));
    this._textBuffer = '';
  }

  // 尾段文本可能等不到标点，用短延时兜底 flush，避免最后半句卡住。
  _scheduleBufferFlush() {
    this._clearBufferFlushTimer();
    if (this._textStreamDone || this._textBuffer.length === 0) return;

    this._bufferFlushTimer = setTimeout(() => {
      this._bufferFlushTimer = null;
      if (this._textStreamDone) return;
      this._flushRemainingBuffer();
      this._tryFlushBuffer();
    }, this._bufferFlushDelay);
  }

  // 统一清理尾段 flush 定时器，防止旧任务继续推进文本。
  _clearBufferFlushTimer() {
    if (this._bufferFlushTimer) {
      clearTimeout(this._bufferFlushTimer);
      this._bufferFlushTimer = null;
    }
  }

  // 只有真正准备发送音频时才启动 Native 播放容器，兼容打断后的重新开始。
  _ensureNativePlayStarted() {
    if (this._nativePlayStarted) return;

    const playGeneration = ++this._nativePlayGeneration;
    this._nativePlayStarted = true;
    this._playing = true;
    NativeModules.AudioStream.config({
      sampleRate: 24000,
      isPCM: true
    });
    NativeModules.AudioStream.play([]).then(() => {
      if (playGeneration !== this._nativePlayGeneration) return;
      this._nativePlayStarted = false;
      this._playing = false;
      if (this._connectionDone && this._ws != null) {
        this.release();
      }
      if (this._callback) {
        this._callback(true);
      }
    });
  }

  // 按“一段文本对应一次 session”的方式串行发送到服务端。
  _sendNextSessionChunk() {
    if (this._sessionTextQueue.length === 0 || this._ws == null) return;

    const chunk = this._sessionTextQueue.shift();
    this._sessionID = this.uuidv4();
    this._sessionActive = true;
    this._state = State.SESSION_ACTIVE;
    this._ensureNativePlayStarted();
    console.log('ttsplayer text queue left', this._sessionTextQueue.length);
    this._ws.send(
      protocol.createStartSessionFrame(this._sessionID, this._config?.voiceType, this._lang)
    );
    this._ws.send(protocol.createTaskRequestFrame(chunk, this._sessionID));
    this._ws.send(protocol.createFinishSessionFrame(this._sessionID));
  }

  // 核心调度器：连接就绪且没有活跃 session 时，消费待发送队列。
  _tryFlushBuffer() {
    if (this._waitingForSessionFinish) return;
    if (this._state !== State.CONNECTED && this._state !== State.SESSION_IDLE) {
      return;
    }

    if (this._sessionTextQueue.length > 0) {
      this._sendNextSessionChunk();
      return;
    }

    if (this._textStreamDone && this._textBuffer === '' && this._connectionDone) {
      console.log('ttsplayer received all audio data.');
      if (this._ws != null && this._state !== State.FINISHING) {
        this._state = State.FINISHING;
        NativeModules.AudioStream.fillBufferDone();
        this._ws.close(this.CloseFrame.NORMAL, 'received all audio data.');
      }
    }
  }

  _play(callback, lang) {
    console.log('ttsplayer play preparing', this._config);

    console.log('ttsplayer stream connecting', this._sessionID);
    this._ws.onopen = () => {
      console.log('ttsplayer stream connecting successfully');
      if (this._ws == null) {
        console.log('ttsplayer _ws is destroyed');
        return;
      }
      this._isConnecting = false;
      if (callback) callback(false);
      this._state = State.CONNECTED;

      if (this._initialText) {
        this._sessionTextQueue.push(...this.splitIntoQueue(this._initialText));
        this._initialText = '';
      }
      protocol.setAudioParams(this._config);
      this._ws.send(protocol.create_start_connection_frame());
      this._tryFlushBuffer();
    };

    this._ws.onmessage = (event) => {
      if (!(event.data instanceof ArrayBuffer)) {
        this.stop();
        if (this._callback) {
          this._callback(true);
        }
        console.log('ttsplayer onmessage error data');
      }
      const result = protocol.receiveResponse(event.data);
      this.parseMessage(result);
    };

    this._ws.onclose = (event) => {
      console.log('ttsplayer onclose: ', event);
      this._isConnecting = false;
      if (this._state !== State.FINISHED) {
        this._state = State.IDLE;
      }
    };

    this._ws.onerror = (error) => {
      console.log('ttsplayer onerror: ', error);
      this.stop();
      if (this._callback) {
        this._callback(true);
      }
    };
  }

  // 解析服务端返回：音频包写入 Native，session 结束后继续推进后续文本。
  parseMessage(result) {
    if (result.event == protocol.Event.TTS_RESPONSE) {
      if (result.payload instanceof Uint8Array) {
        let start = Date.now();
        let raw = [];
        raw.push(...result.payload);
        if (Platform.OS == 'android') {
          raw = JSON.stringify(raw);
        }
        if (this._ws != null && !this._dropCurrentSessionAudio) {
          NativeModules.AudioStream.fillBuffer(raw);
        }
        let end = Date.now();
        // console.log(`convertCost: ${end - start} ms.`);
      }
    } else if (result.event == protocol.Event.SESSION_FINISHED) {
      console.log('ttsplayer onmessage SESSION_FINISHED');
      this._sessionActive = false;
      this._state = State.SESSION_IDLE;
      if (this._waitingForSessionFinish) {
        this._waitingForSessionFinish = false;
        this._dropCurrentSessionAudio = false;
        this._tryFlushBuffer();
        return;
      }
      this._tryFlushBuffer();
    } else if (
      result.event == protocol.Event.CONNECTION_FAILED ||
      result.event == protocol.Event.SESSION_FAILED ||
      result.event == protocol.Event.CodeClientError ||
      result.event == protocol.Event.CodeServerError ||
      result.event == protocol.Event.CodeSessionError ||
      result.event == protocol.Event.CodeInvalidReqError
    ) {
      console.log('ttsplayer onmessage parsed:', result.event);
      console.log('ttsplayer onmessage error:', result.payload.error);
      Toast.show(`试听出现错误${result.event}, 请重试！`);
      if (this._ws != null) {
        NativeModules.AudioStream.fillBufferDone();
        this._ws.close(result.event, 'something wrong', result.event);
      }
    } else if (
      result.event == protocol.Event.TTS_SENTENCE_START ||
      result.event == protocol.Event.TTS_SENTENCE_END
    ) {
      console.log('ttsplayer onmessage parsed:', result.event);
      // 返回解析后的分句字符串。一般来说，火山会按照中文的结束标点符号分句。
      if (result.event == protocol.Event.TTS_SENTENCE_END) {
        console.log('ttsplayer onmessage text:', result.payload.text);
      }
    } else if (
      result.event == protocol.Event.START_CONNECTION ||
      result.event == protocol.Event.FINISH_CONNECTION ||
      result.event == protocol.Event.CONNECTION_STARTED ||
      result.event == protocol.Event.CONNECTION_FINISHED ||
      result.event == protocol.Event.START_SESSION ||
      result.event == protocol.Event.FINISH_SESSION ||
      result.event == protocol.Event.SESSION_STARTED ||
      result.event == protocol.Event.TASK_REQUEST
    ) {
      console.log('ttsplayer onmessage parsed:', result.event);
    } else {
      console.log('ttsplayer onmessage unknown event:', result.event);
      if (this._ws != null) {
        NativeModules.AudioStream.fillBufferDone();
        this._ws.close(result.event, 'unknown wrong', result.event);
      }
    }
  }

  // 打断当前轮次：立即停止本地播放并清掉待播文本，但保留当前 WS 连接。
  interruptRound() {
    console.log('ttsplayer interruptRound');
    this._clearBufferFlushTimer();
    this._textBuffer = '';
    this._sessionTextQueue = [];
    this._playing = false;
    this._nativePlayGeneration++;
    this._nativePlayStarted = false;
    NativeModules.AudioStream.stop();
    if (this._sessionActive) {
      this._waitingForSessionFinish = true;
      this._dropCurrentSessionAudio = true;
    } else if (this._state === State.CONNECTED || this._state === State.SESSION_IDLE) {
      this._waitingForSessionFinish = false;
      this._dropCurrentSessionAudio = false;
      this._state = State.SESSION_IDLE;
    }
  }

  // 关闭流式入口并尝试优雅收尾；真正的资源释放仍由 stop/release 处理。
  closeConnection() {
    console.log('ttsplayer closeConnection');
    this._clearBufferFlushTimer();
    this._textStreamDone = true;
    this._connectionDone = true;
    this._flushRemainingBuffer();
    if (!this._sessionActive && !this._waitingForSessionFinish) {
      if (this._state === State.CONNECTED) {
        this._state = State.SESSION_IDLE;
      }
      this._tryFlushBuffer();
    }
  }

  stop() {
    console.log('ttsplayer stop');
    this.release();
  }

  release() {
    console.log('ttsplayer release');
    this._clearBufferFlushTimer();
    this._nativePlayGeneration++;
    this._nativePlayStarted = false;
    this._waitingForSessionFinish = false;
    this._dropCurrentSessionAudio = false;
    this._sessionActive = false;
    this._state = State.FINISHED;
    NativeModules.AudioStream.stop();
    this._playing = false;
    this._filename = null;
    this._initialText = '';
    this._textBuffer = '';
    this._sessionTextQueue = [];
    this._config = null;
    protocol.setAudioParams(null);
    if (this._ws != null) {
      console.log('ttsplayer close ws');
      this._ws.close(this.CloseFrame.NORMAL, 'stop');
    }
    this._ws = null;
  }

  getDuration() {
    console.log('ttsplayer getDuration');
    return -1;
  }

  // 生成 UUID
  uuidv4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}



// WEBPACK FOOTER //
// ../biz-micro-services-ui/packages/basement/audio/ttsplayer.js